import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../context/AuthContext';
import BRAND from '../brand';

// Quick-login credentials shown on the login page so you can
// always get in without memorising passwords.
// REMOVE THIS section before going public to real customers.
const DEMO_ACCOUNTS = [
  { role: 'Admin',    email: 'admin@retflow.ai',  password: 'admin123',  path: '/admin',     color: '#ef4444', desc: 'Full platform control' },
  { role: 'Worker',   email: 'worker@retflow.ai', password: 'worker123', path: '/worker',    color: '#3b82f6', desc: 'Sales pipeline & leads' },
  { role: 'Customer', email: '(sign up)',          password: '',          path: '/signup',    color: BRAND.accent, desc: 'Bot dashboard (needs signup)' },
];

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const inp = {
    width: '100%', padding: '0.75rem 1rem',
    background: '#18181b', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, color: '#fafafa', fontSize: '0.9rem',
    fontFamily: 'Geist, sans-serif', outline: 'none',
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Trim whitespace — a common cause of "invalid credentials"
    const result = login(email.trim(), password);
    setLoading(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    // Redirect based on role
    if (result.role === ROLES.ADMIN)  { navigate('/admin');     return; }
    if (result.role === ROLES.WORKER) { navigate('/worker');    return; }
    navigate('/dashboard');
  };

  // One-click login for a demo account
  const quickLogin = (account) => {
    if (!account.password) { navigate('/signup'); return; }
    setEmail(account.email);
    setPassword(account.password);
    setError('');
    setLoading(true);
    const result = login(account.email, account.password);
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    navigate(account.path);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#09090b', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: 'Geist, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: '1.75rem', color: '#fafafa', marginBottom: '0.5rem' }}>
            {BRAND.name}<span style={{ color: BRAND.accent }}>.</span>
          </div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 600, color: '#fafafa', marginBottom: '0.3rem' }}>Sign in</h1>
          <p style={{ color: '#71717a', fontSize: '0.82rem' }}>
            One login for all account types — admin, worker, and customer.
          </p>
        </div>

        {/* Quick-login buttons */}
        <div style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#71717a', marginBottom: '0.65rem' }}>Quick login</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {DEMO_ACCOUNTS.map(account => (
              <button
                key={account.role}
                onClick={() => quickLogin(account)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 0.85rem', background: '#18181b', border: `1px solid ${account.color}22`, borderRadius: 8, cursor: 'pointer', fontFamily: 'Geist, sans-serif', transition: 'all 0.15s', textAlign: 'left' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${account.color}55`; e.currentTarget.style.background = `${account.color}08`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = `${account.color}22`; e.currentTarget.style.background = '#18181b'; }}
              >
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#fafafa' }}>{account.role}</div>
                  <div style={{ fontSize: '0.7rem', color: '#71717a', marginTop: 1 }}>{account.desc}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.7rem', color: account.color, fontFamily: 'Geist Mono, monospace' }}>{account.email}</div>
                  {account.password && <div style={{ fontSize: '0.68rem', color: '#3f3f46', marginTop: 1 }}>{account.password}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Login form */}
        <div style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.75rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: '#71717a', marginBottom: '1rem' }}>Or enter credentials manually</div>

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.82rem', color: '#fca5a5', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 500, color: '#a1a1aa', display: 'block', marginBottom: '0.4rem' }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@retflow.ai"
                required
                style={inp}
                onFocus={e => e.target.style.borderColor = `rgba(245,158,11,0.6)`}
                onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 500, color: '#a1a1aa', display: 'block', marginBottom: '0.4rem' }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={inp}
                onFocus={e => e.target.style.borderColor = `rgba(245,158,11,0.6)`}
                onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', padding: '0.8rem', background: BRAND.accent, color: BRAND.accentText, border: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Geist, sans-serif', opacity: loading ? 0.7 : 1, marginTop: '0.25rem' }}
            >
              {loading ? 'Signing in...' : 'Sign in →'}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.82rem', color: '#71717a' }}>
          New customer?{' '}
          <span style={{ color: BRAND.accent, cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate('/signup')}>
            Create an account
          </span>
        </p>
        <p style={{ textAlign: 'center', marginTop: '0.5rem' }}>
          <span style={{ fontSize: '0.78rem', color: '#3f3f46', cursor: 'pointer' }} onClick={() => navigate('/')}>← Back to home</span>
        </p>
      </div>
    </div>
  );
}